export type PoolJobCta =
  | 'book'
  | 'unlock_missing_reqs'
  | 'unlock_onboarding'
  | 'unlock_onboarding_review'
  | null;

export type PoolJobTapAction =
  | 'none'
  | 'book'
  | 'navigateMissingReqs'
  | 'acceptThenOnboarding'
  | 'navigateOnboarding'
  | 'navigateReview'
  | 'joinThenBook'
  | 'joinThenOnboarding'
  | 'joinThenReview';

export type PoolJobResolution = {
  cta: PoolJobCta;
  action: PoolJobTapAction;
};

export type PoolJobResolverInput = {
  poolId: number | null | undefined;
  canBookJob: boolean | null | undefined;
  requiresProReview: boolean | null | undefined;
  hasPoolPq: boolean;
  onboardingStatus: string | null | undefined;
  onboardingStatusUnavailable?: boolean;

  onlyMissingPoolComplete: boolean;
};

const ONBOARDING_STATUS = {
  notAccepted: 'not_accepted',
  complete: 'complete',
  readyForReview: 'ready_for_review',
} as const;

const TERMINAL_STATUSES = ['rejected', 'removed'];

export const resolvePoolJob = (input: PoolJobResolverInput): PoolJobResolution => {
  const { poolId, canBookJob, requiresProReview, hasPoolPq, onboardingStatus } = input;

  // Non-pool job: render the existing Book button unchanged.
  if (poolId === null || poolId === undefined) {
    return { cta: null, action: 'none' };
  }

  if (hasPoolPq) {
    if (input.onboardingStatusUnavailable) {
      return { cta: null, action: 'none' };
    }
    if (onboardingStatus === ONBOARDING_STATUS.complete) {
      return canBookJob
        ? { cta: 'book', action: 'book' }
        : { cta: 'unlock_missing_reqs', action: 'navigateMissingReqs' };
    }
    if (onboardingStatus === ONBOARDING_STATUS.notAccepted) {
      return { cta: 'unlock_onboarding', action: 'acceptThenOnboarding' };
    }
    if (onboardingStatus === ONBOARDING_STATUS.readyForReview) {
      return { cta: 'unlock_onboarding_review', action: 'navigateReview' };
    }
    // Defensive: the BE should have filtered rejected/removed pool jobs.
    if (onboardingStatus && TERMINAL_STATUSES.includes(onboardingStatus)) {
      return { cta: null, action: 'none' };
    }
    // Active onboarding (credentialing, w2_payroll_setup, needs_orientation) or any
    // other in-progress status: go straight to onboarding, no Join/Accept call.
    return { cta: 'unlock_onboarding', action: 'navigateOnboarding' };
  }

  // No pool PQ: the pro must Join (FE-4) before anything else.
  if (!requiresProReview && input.onlyMissingPoolComplete) {
    return { cta: 'book', action: 'joinThenBook' };
  }
  return requiresProReview
    ? { cta: 'unlock_onboarding_review', action: 'joinThenReview' }
    : { cta: 'unlock_onboarding', action: 'joinThenOnboarding' };
};

export const getPoolJobCta = (input: PoolJobResolverInput): PoolJobCta => resolvePoolJob(input).cta;

export const getPoolJobTapAction = (input: PoolJobResolverInput): PoolJobTapAction =>
  resolvePoolJob(input).action;

export const poolCompleteSlug = (poolId: number): string => `professional_pool_${poolId}_complete`;

export const isOnlyMissingPoolCompleteRequirement = (
  poolId: number,
  missingConditions: ReadonlyArray<{ slug?: string | null }> | null | undefined,
): boolean => {
  const targetSlug = poolCompleteSlug(poolId);
  const slugs = (missingConditions ?? []).map((condition) => condition.slug);
  return slugs.length === 1 && slugs[0] === targetSlug;
};

const UNLOCK_LABEL = 'Unlock';
const BOOK_LABEL = 'Book shift';

/** Button copy for a CTA state. `null` when there is no pool CTA to render. */
export const poolJobCtaLabel = (cta: PoolJobCta): string | null => {
  switch (cta) {
    case 'book':
      return BOOK_LABEL;
    case 'unlock_missing_reqs':
    case 'unlock_onboarding':
    case 'unlock_onboarding_review':
      return UNLOCK_LABEL;
    case null:
      return null;
  }
};
